import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useLayoutEffect, useRef } from 'react';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ActionListRow from './ActionListRow.js';
import ActionListHeader from './ActionListHeader.js';
function getTimestamps(actions, actionIds, actionId) {
    const idx = actionIds.indexOf(actionId);
    const prevActionId = actionIds[idx - 1];
    return {
        current: actions[actionId].timestamp,
        previous: idx ? actions[prevActionId].timestamp : 0,
    };
}
function scrollToBottom(node) {
    node.scrollTop = node.scrollHeight;
}
export default function ActionList({ actions, actionIds, isWideLayout, onToggleAction, skippedActionIds, selectedActionId, startActionId, onSelect, onSearch, searchValue, currentActionId, hideMainButtons, hideActionButtons, onCommit, onSweep, onJumpToState, lastActionId, onReorderAction, }) {
    const nodeRef = useRef(null);
    const prevLastActionId = useRef(undefined);
    useLayoutEffect(() => {
        if (nodeRef.current && prevLastActionId.current !== lastActionId) {
            const { scrollTop, offsetHeight, scrollHeight } = nodeRef.current;
            if (Math.abs(scrollHeight - (scrollTop + offsetHeight)) < 50) {
                scrollToBottom(nodeRef.current);
            }
        }
        prevLastActionId.current = lastActionId;
    }, [lastActionId]);
    const setNodeRef = useCallback((node) => {
        if (node && !nodeRef.current) {
            scrollToBottom(node);
        }
        nodeRef.current = node;
    }, []);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    }), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const handleDragEnd = useCallback(({ active, over }) => {
        if (over && active.id !== over.id) {
            const activeIndex = actionIds.indexOf(active.id);
            const overIndex = actionIds.indexOf(over.id);
            const beforeActionId = overIndex < activeIndex
                ? over.id
                : overIndex < actionIds.length - 1
                    ? actionIds[overIndex + 1]
                    : actionIds.length;
            onReorderAction(active.id, beforeActionId);
        }
    }, [actionIds, onReorderAction]);
    const lowerSearchValue = searchValue && searchValue.toLowerCase();
    const filteredActionIds = searchValue
        ? actionIds.filter((id) => actions[id].action.type
            .toLowerCase()
            .includes(lowerSearchValue))
        : actionIds;
    return (_jsxs("div", { "data-testid": "actionList", css: [
            (theme) => ({
                flexBasis: '40%',
                flexShrink: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
                borderBottomWidth: '3px',
                borderBottomStyle: 'double',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.BACKGROUND_COLOR,
                borderColor: theme.LIST_BORDER_COLOR,
            }),
            isWideLayout && {
                flexBasis: '40%',
                borderBottom: 'none',
                borderRightWidth: '3px',
                borderRightStyle: 'double',
            },
        ], children: [_jsx(ActionListHeader, { onSearch: onSearch, onCommit: onCommit, onSweep: onSweep, hideMainButtons: hideMainButtons, hasSkippedActions: skippedActionIds.length > 0, hasStagedActions: actionIds.length > 1, searchValue: searchValue }), _jsx("div", { "data-testid": "actionListRows", css: { overflow: 'auto' }, ref: setNodeRef, children: _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, modifiers: [restrictToFirstScrollableAncestor], children: _jsx(SortableContext, { items: filteredActionIds, strategy: verticalListSortingStrategy, children: filteredActionIds.map((actionId) => (_jsx(SortableItem, { actionId: actionId, children: _jsx(ActionListRow, { actionId: actionId, isInitAction: !actionId, isSelected: (startActionId !== null &&
                                    actionId >= startActionId &&
                                    actionId <= selectedActionId) ||
                                    actionId === selectedActionId, isInFuture: actionIds.indexOf(actionId) >
                                    actionIds.indexOf(currentActionId), onSelect: (e) => onSelect(e, actionId), timestamps: getTimestamps(actions, actionIds, actionId), action: actions[actionId].action, onToggleClick: () => onToggleAction(actionId), onJumpClick: () => onJumpToState(actionId), onCommitClick: () => onCommit(), hideActionButtons: hideActionButtons, isSkipped: skippedActionIds.includes(actionId) }) }, actionId))) }) }) })] }, "actionList"));
}
function SortableItem({ children, actionId }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: actionId });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (_jsx("div", { ref: setNodeRef, style: style, ...attributes, ...listeners, children: children }));
}
